import './SignUpPage.scss';

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as LogoImage } from '@assets/images/logo_icon.svg';
import Button from '@components/Button';
import { RoutePath } from '@constants/route-path';
import { IClient } from '@modulate/shared/src/models/client';
import { useInjection } from '@src/components/Injection';
import { useAuth } from '@src/contexts/authContext';
import { auth } from '@src/firebase/firebase';
import { CLIENT_SERVICE, ClientService } from '@src/services/client.service';
import { CLINICS_SERVICE, ClinicsService } from '@src/services/clinics.service';
import { doCreateUserWithEmailAndPassword, doSignInWithEmailAndPassword } from '../firebase/auth';

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePassword = (password: string): boolean => {
  const minLength = 8;
  const conditions = [
    (pw) => pw.length >= minLength,
    (pw) => /[A-Z]/.test(pw),
    (pw) => /[a-z]/.test(pw),
    (pw) => /[0-9]/.test(pw),
    (pw) => /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  ];

  return conditions.every((condition) => condition(password));
};

const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone);
};

function SignUpPage() {
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clientService = useInjection<ClientService>(CLIENT_SERVICE);

  useEffect(() => {
    if (isCodeValid && isPhoneValid && isEmailValid && isPasswordValid && firstName && lastName) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [isCodeValid, firstName, lastName, isEmailValid, isPasswordValid]);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const codeValue = e.target.value;
    setCode(codeValue);
    setIsCodeValid(codeValue.length > 0);
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phoneValue = e.target.value.replace(/[^\d]/g, '');
    if (phoneValue.length > 10) {
      e.preventDefault();
    } else {
      setPhoneNumber(phoneValue);
      setIsPhoneValid(validatePhone(phoneValue));
    }
  };

  const formatPhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value.padEnd(10, '_');
    const match = phone.match(/^(.{0,3})(.{0,3})(.{0,4})$/);
    if (phone === '') {
      setPhoneNumber(phone);
    } else if (match) {
      setPhoneNumber(`${match[1]}-${match[2]}-${match[3]}`);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    setIsEmailValid(validateEmail(emailValue));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const passwordValue = e.target.value;
    setPassword(passwordValue);
    setIsPasswordValid(validatePassword(passwordValue));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const { initializeUser } = useAuth();
  const clinicsService = useInjection<ClinicsService>(CLINICS_SERVICE);

  const handleSubmit = async (e: React.FormEvent, initUser) => {
    e.preventDefault();
    try {
      const isCodeValid = await clinicsService.validateCode(code);
      if (isCodeValid.exists) {
        await doCreateUserWithEmailAndPassword(email, password);

        const newClient: IClient = {
          firstName: firstName,
          lastName: lastName,
          email: email,
          phone: phoneNumber,
          uuid: auth.currentUser.uid,
          status: 'Admin',
          role: 'Orthotist',
          clinicId: 0,
          signUpCode: code,
        };

        await clientService.create(newClient);
        await initUser(auth.currentUser, clientService);

        setError(null);
        navigate(RoutePath.PatientListPage);
      } else {
        setError('Your sign up code is invalid. Contact you administrator.');
      }
    } catch (err) {
      setError('Failed to sign up. Please check your information.');
      console.error(err);
    }
  };

  return (
    <div className="modSignUp">
      <div className="modSignUp__box">
        <LogoImage className="modSignUp__logo" />
        <div className="modSignUp__title">Welcome</div>
        <form onSubmit={(e) => handleSubmit(e, initializeUser)} className="modSignUp__form">
          <div className="modSignUp__inputName">Sign up code:</div>
          <input className="modSignUp__input" value={code} onChange={handleCodeChange} />
          <div className="modSignUp__FirstLastName">
            <div className="modSignUp__name">
              <div className="modSignUp__inputName">First name:</div>
              <input className="modSignUp__input" value={firstName} onChange={handleFirstNameChange} />
            </div>
            <div className="modSignUp__name">
              <div className="modSignUp__inputName">Last name:</div>
              <input className="modSignUp__input" value={lastName} onChange={handleLastNameChange} />
            </div>
          </div>
          <div className="modSignUp__inputName">Phone (10 digits):</div>
          <input
            className="modSignUp__input"
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onBlur={formatPhoneNumber}
            placeholder="___-___-____"
          />
          <div className="modSignUp__inputName">Email:</div>
          <input
            className={`modSignUp__input ${!isEmailValid ? 'invalid' : ''}`}
            type="email"
            value={email}
            onChange={handleEmailChange}
          />
          {/*!isEmailValid && <p style={{ color: 'red' }}>Please enter a valid email address.</p>*/}
          <div className="modSignUp__inputName">Password (8+ chars, upper, lower, num & special):</div>
          <div className="modSignUp__passwordInput">
            <input
              className={`modSignUp__input ${!isPasswordValid ? 'invalid' : ''}`}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
            />
            <button type="button" className="modSignUp__passwordToggle" onClick={togglePasswordVisibility}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <div className="modSignUp__inputName"></div>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {isButtonDisabled && (
            <Button disabled={true} fullWidth={true}>
              Sign up
            </Button>
          )}
          {!isButtonDisabled && (
            <Button disabled={false} type="submit" fullWidth={true}>
              Sign up
            </Button>
          )}
        </form>
        <div className="modSignUp__logIn">
          Already have an account? <a href="/login">Log in</a>
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
